/**
 * Preset tank cards (snippets/kx-preset-card.liquid, rendered by
 * sections/kx-featured-tanks.liquid and sections/kx-tank-presets.liquid):
 * merchandised tanks that deep link into the Build Your Own Tank builder.
 *
 * Card prices are COMPUTED with the shared build module (kx-build-core.js:
 * the builder's own deep-link reader, settled build and rules, KX Console
 * bundles design §2 (a) and §5.2) from the same data files the builder prices
 * from (tank-catalogue.json, tank-size-rules.json, tank-sku-map.json) and
 * kingspan-tank-core.js (paint tiers, the six pumps and the builder offers as
 * the shop's kx_promo.offers metafield runs them), so a card can never drift
 * from the builder total. The KX Console's Bundle Builder and
 * .catalog-source/check-bundles.js price with the same module.
 *
 * Markup contract (the snippet and the sections: kx-featured-tanks,
 * kx-tank-presets, and the urban landing page's popular cards when they come
 * from the KX Console):
 *  - a root [data-kx-preset-cards] with data-catalogue-url, data-size-rules-url,
 *    data-sku-map-url, data-core-url and data-build-core-url (asset_url values);
 *  - cards [data-kxft-link="<builder link>"] holding [data-kxft-price],
 *    [data-kxft-deal], [data-kxft-deal-note] and [data-kxft-fire-note], all
 *    hidden until priced. A card without data-kxft-link (a product card) keeps
 *    its server-rendered price;
 *  - optional filters [data-kxtp-filters] with chip buttons
 *    [data-kxtp-filter="kind|band"][data-kxtp-value], over cards carrying
 *    data-kxtp-kind and data-kxtp-band.
 *
 * Loaded with defer; safe to load more than once and on section reloads in
 * the theme editor. Data files, the core and the build module load once per
 * page.
 */
(function () {
  'use strict';

  if (window.KxPresetCards) {
    window.KxPresetCards.scan(document);
    return;
  }

  // A link with no field the builder reads (?preset= alone): the builder opens
  // with its defaults, so the card prices those (kx-build-core parseLink
  // gives null for it).
  function defaultBuild() {
    return { config: {}, qty: 1, preset: null, urlType: null };
  }

  function editLink(href) {
    try {
      return !!new URL(String(href), window.location.href).searchParams.get('kut_edit');
    } catch (err) {
      return /[?&]kut_edit=[^&#]/.test(String(href));
    }
  }

  var scriptPromises = {};
  var dataPromises = {};

  /**
   * A classic script that defines window[name], injected once (re-running it
   * is harmless). The core (paint tiers, palettes, sku keys and the offers)
   * and the build module come from the builder's own files; nothing else
   * loads them on the home or collection pages until the chat opens.
   */
  function loadGlobal(url, name, file) {
    if (window[name]) return Promise.resolve(window[name]);
    if (scriptPromises[name]) return scriptPromises[name];
    var p = new Promise(function (resolve, reject) {
      var el = document.createElement('script');
      el.src = url;
      el.onload = function () {
        if (window[name]) resolve(window[name]);
        else reject(new Error(name + ' missing'));
      };
      el.onerror = function () { reject(new Error(file + ' failed to load')); };
      document.head.appendChild(el);
    });
    scriptPromises[name] = p;
    p.catch(function () { delete scriptPromises[name]; });
    return p;
  }

  /** The three data files, the core and the build module, fetched once per set of URLs. */
  function loadData(urls) {
    var key = [urls.catalogue, urls.sizeRules, urls.skuMap, urls.core, urls.buildCore].join('|');
    if (!dataPromises[key]) {
      dataPromises[key] = Promise.all([
        fetch(urls.catalogue).then(function (r) { return r.json(); }),
        // Only for snapping a link's size the way the builder does; without it the
        // link's own size is priced.
        fetch(urls.sizeRules).then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; }),
        fetch(urls.skuMap).then(function (r) { return r.json(); }).catch(function () { return null; }),
        loadGlobal(urls.core, 'KingspanTankCore', 'kingspan-tank-core.js'),
        loadGlobal(urls.buildCore, 'KXBuildCore', 'kx-build-core.js')
      ]);
      dataPromises[key].catch(function () { delete dataPromises[key]; });
    }
    return dataPromises[key];
  }

  /**
   * The builder's total for this link, with the offers the policy runs:
   * KXBuildCore.computePrice (kx-build-core.js), the builder's own deep-link
   * reader, settled build and rules over the same data files and
   * kingspan-tank-core.js. null when the link cannot be priced.
   */
  function priceOf(build, data, href, policy) {
    try {
      // A ?kut_edit= link edits a cart line in the builder: its price is that line's, which a card
      // cannot know, so it shows none (theme review B1B-08; neither the console nor the generator
      // writes one).
      if (editLink(href)) return null;
      var link = build.parseLink(href) || defaultBuild();
      var price = build.computePrice(link, data, policy);
      return price && isFinite(price.total) ? price : null;
    } catch (err) {
      // One card the module cannot price keeps its price hidden; the rest still price.
      console.warn('kx-preset-cards: cannot price ' + href, err);
      return null;
    }
  }

  function money(n) {
    var digits = Math.round(n * 100) % 100 === 0 ? 0 : 2;
    return new Intl.NumberFormat('en-AU', {
      style: 'currency', currency: 'AUD',
      minimumFractionDigits: digits, maximumFractionDigits: digits
    }).format(n);
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function fireNoteText(fire) {
    if (!fire) return '';
    var text = 'Priced for ' + fire.label + '.';
    if (fire.others.length) {
      text += ' The fire fitting follows your delivery state (' + fire.others.map(function (o) {
        return o.label + ' ' + money(o.price);
      }).join(', ') + ').';
    }
    return text;
  }

  function setHidden(el, hidden) {
    if (!el) return;
    if (hidden) el.setAttribute('hidden', '');
    else el.removeAttribute('hidden');
  }

  function priceCards(root) {
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-kxft-link]'));
    if (!cards.length) return;
    var urls = {
      catalogue: root.getAttribute('data-catalogue-url'),
      sizeRules: root.getAttribute('data-size-rules-url'),
      skuMap: root.getAttribute('data-sku-map-url'),
      core: root.getAttribute('data-core-url'),
      buildCore: root.getAttribute('data-build-core-url')
    };
    if (!urls.catalogue || !urls.skuMap || !urls.core || !urls.buildCore) return;

    loadData(urls).then(function (results) {
      var core = results[3];
      var build = results[4];
      var data = { catalogue: results[0], sizeRules: results[1], skuMap: results[2], core: core };
      (function render() {
        // The page's offers (window.KingspanTankOffers, kx_promo.offers), judged now.
        var policy = core.promoPolicy();
        cards.forEach(function (card) {
          var href = card.getAttribute('data-kxft-link');
          if (!href) return;
          var price = priceOf(build, data, href, policy);
          if (!price) return;
          // One chip per offer that takes something off (tank offer, pump offer),
          // with the builder's wording; the card is priced for one state and the
          // builder starts on NSW / ACT, so the note names the states the whole
          // saving applies in (from the offers' own regions).
          var lines = price.labels;

          var el = card.querySelector('[data-kxft-price]');
          if (el) {
            if (lines.length) {
              el.innerHTML = '<s>' + money(price.full) + '</s>' + money(price.total);
            } else {
              el.textContent = money(price.total);
            }
            setHidden(el, false);
          }
          var deal = card.querySelector('[data-kxft-deal]');
          var note = card.querySelector('[data-kxft-deal-note]');
          if (lines.length) {
            if (deal) {
              deal.innerHTML = lines.map(function (l) {
                return '<span class="kxft__deal" data-kxft-offer="' + escapeHtml(l.offer) + '">' +
                  escapeHtml(l.label + ' — ' + l.item) + '</span>';
              }).join('');
              setHidden(deal, false);
            }
            if (note) {
              note.textContent = price.terms;
              setHidden(note, false);
            }
          } else {
            if (deal) { deal.innerHTML = ''; setHidden(deal, true); }
            setHidden(note, true);
          }
          var fireNote = card.querySelector('[data-kxft-fire-note]');
          if (fireNote) {
            var text = fireNoteText(price.fire);
            fireNote.textContent = text;
            setHidden(fireNote, !text);
          }
        });
        // An offer that starts or ends while the page is open: re-price then, as
        // checkout starts or stops it at that instant.
        if (core.onPromoChange) core.onPromoChange(policy, render);
      })();
    }).catch(function (err) {
      console.warn('kx-preset-cards: price compute failed', err);
    });
  }

  /** Client-side filter chips: one group per [data-kxtp-filter] kind; groups combine. */
  function bindFilters(root) {
    var bar = root.querySelector('[data-kxtp-filters]');
    if (!bar) return;
    var chips = Array.prototype.slice.call(bar.querySelectorAll('[data-kxtp-filter]'));
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-kxtp-kind]'));
    var empty = root.querySelector('[data-kxtp-empty]');
    var count = root.querySelector('[data-kxtp-count]');
    var chosen = {};
    // A page with product cards (KX Console, B1b) counts cards, not tanks.
    var unit = cards.some(function (card) { return card.getAttribute('data-kxtp-kind') === 'product'; }) ? 'card' : 'tank';

    function apply() {
      var shown = 0;
      cards.forEach(function (card) {
        var ok = Object.keys(chosen).every(function (group) {
          return !chosen[group] || card.getAttribute('data-kxtp-' + group) === chosen[group];
        });
        setHidden(card, !ok);
        if (ok) shown++;
      });
      setHidden(empty, shown > 0);
      if (count) count.textContent = shown === 1 ? '1 ' + unit + ' shown' : shown + ' ' + unit + 's shown';
    }

    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        var group = chip.getAttribute('data-kxtp-filter');
        chosen[group] = chip.getAttribute('data-kxtp-value') || '';
        chips.forEach(function (c) {
          if (c.getAttribute('data-kxtp-filter') !== group) return;
          c.setAttribute('aria-pressed', c === chip ? 'true' : 'false');
        });
        apply();
      });
    });
  }

  /**
   * Pricing loads the SKU map (about 160 KB gzipped), the catalogue, the size
   * rules and the core, so it starts only once the cards come near the
   * viewport (at once where IntersectionObserver is missing).
   */
  function whenNear(root, fn) {
    if (typeof window.IntersectionObserver !== 'function') {
      fn();
      return;
    }
    var io = new window.IntersectionObserver(function (entries) {
      if (!entries.some(function (e) { return e.isIntersecting; })) return;
      io.disconnect();
      fn();
    }, { rootMargin: '800px 0px' });
    io.observe(root);
  }

  function initRoot(root) {
    if (!root || root.__kxPresetCards) return;
    root.__kxPresetCards = true;
    bindFilters(root);
    whenNear(root, function () { priceCards(root); });
  }

  function scan(scope) {
    var base = scope || document;
    var roots = Array.prototype.slice.call(base.querySelectorAll('[data-kx-preset-cards]'));
    if (base.getAttribute && base.hasAttribute('data-kx-preset-cards')) roots.unshift(base);
    roots.forEach(initRoot);
  }

  window.KxPresetCards = { scan: scan };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(document); });
  } else {
    scan(document);
  }
  // Theme editor: a section added or re-rendered brings new cards.
  document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
})();
